import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { MdmObjectDto, MdmObjectJobDtoInput, SeverityType, StatusType, useCreateJobMutation, useUpdateJobMutation } from "gql/generated/types";
import { GET_JOBS_BY_OBJECT_ID } from "gql/job.operations";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { errorToaster, getFormErrorMessage, successToaster } from "../utils/utils";
import { priorityType, statusType } from "../utils/const";

const schema = z.object({
  status: z.string().min(1, "Required"),
  severity: z.string().min(1, "Required"),
  description: z.string().min(1, "Required"),
  title: z.string().min(1, "Required"),
  startDate: z.string(),
  endDate: z.string(),
  objectId: z.string().optional(),
  userId: z.string().optional(),
  id: z.number().optional(),
})

type JobFormProps = {
  object: MdmObjectDto;
  closeDialog: () => void;
  job: MdmObjectJobDtoInput | null
};


const JobForm: React.FC<JobFormProps> = ({ object, closeDialog, job }) => {
  const defaultValues: MdmObjectJobDtoInput = {
    status: StatusType.Todo,
    severity: SeverityType.Medium,
    description: "",
    title: "",
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    objectId: object.id,
    userId: job?.id,
  };

  const {control, handleSubmit, formState: { errors }, reset} = useForm<MdmObjectJobDtoInput>({
    defaultValues: job ?? defaultValues,
    resolver: zodResolver(schema),
  });

  const [createJobMutation] = useCreateJobMutation({
    onCompleted: () => successToaster("Job created successfully"),
    onError: errorToaster,
  });
  
  const [updateJobMutation] = useUpdateJobMutation({
    onCompleted: () => successToaster("Job updated successfully"),
    onError: errorToaster,
  });


  const handleCreateJob: SubmitHandler<MdmObjectJobDtoInput> = formData => {
    createJobMutation({
      variables: {
        input: formData,
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_JOBS_BY_OBJECT_ID, variables: {id: object.id}}, (oldData) => ({ 
          allJobsByObjectId: [...oldData.allJobsByObjectId, qData?.createJob]
        }) );
      }
    });
    reset();
    closeDialog();
  };

  const handleUpdateJob: SubmitHandler<MdmObjectJobDtoInput> = formData => {
    updateJobMutation({
      variables: {
        input: formData,
      },
    });
    reset();
    closeDialog();
  };

  return (
    <form className="flex flex-wrap">
      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="Status" col={4}>
            <Dropdown
              value={field.value}
              options={Object.keys(statusType)}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="severity"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="Priority" col={4}>
            <Dropdown
              value={field.value}
              options={Object.keys(priorityType)}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="title"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="Title" col={4}>
            <InputText
              value={field.value ?? ""}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="startDate"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="Start Date" col={4}>
            <Calendar
              value={new Date(field.value)}
              dateFormat="yy-mm-dd"
              onChange={e => field.onChange((e.target.value as Date).toISOString())}
            />
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="endDate"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="End Date">
            <Calendar
              value={new Date(field.value)}
              dateFormat="yy-mm-dd"
              onChange={e => field.onChange((e.target.value as Date).toISOString())}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="Description" className="w-full">
            <InputTextarea
              value={field.value ?? ""}
              rows={4}
              maxLength={255}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      {job ? (
        <Button
          label="Update"
          className="mt-3 ml-2 p-button-success"
          onClick={handleSubmit(handleUpdateJob)}
        />
      ) : (
        <Button
          label="Create"
          className="mt-3 ml-2 p-button-success"
          onClick={handleSubmit(handleCreateJob)}
        />
      )}
    </form>
  );
}

export default JobForm;